"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TutorialChatHelper = void 0;
class TutorialChatHelper {
    static updateChatTools() {
        if (!this.chatComponent) {
            console.warn('Chat component is not initialized.');
            return;
        }
        this.chatComponent.updateToolData('tutorialFormEdit', TutorialChatHelper.getSanitizedFormData.call(this));
    }
    static runExamplePrompt(prompt) {
        if (!this.chatComponent) {
            console.warn('Chat component is not initialized.');
            return;
        }
        this.chatStarted = true;
        TutorialChatHelper.updateChatTools.call(this);
        this.chatComponent.appendInputTextToChat(prompt, true);
    }
    /**
     * Get form data sanitized for AI tool - excludes binary file objects
     * (logo, instructionFile, sourceFile, avatar, screenshot, video, knowledgeIds, sourcePages*, sourceText)
     * The attached onboarding is supplied too, so the AI follows it just like the tutorial scan does.
     */
    static getSanitizedFormData() {
        const sanitizedForm = {
            name: this.form.name,
            description: this.form.description,
            introText: this.form.introText,
            tutorialGoal: this.form.tutorialGoal,
            active: this.form.active,
            categoryId: this.form.categoryId,
            source: this.form.source
        };
        if (this.onboardingAttached) {
            sanitizedForm.onboardingPayload = this.onboardingAttached;
        }
        if (this.steps && this.steps.length > 0) {
            sanitizedForm.steps = this.steps.map(step => ({
                title: step.title,
                description: step.description,
                order: step.order,
                sections: step.sections.map(section => {
                    var _a;
                    return ({
                        title: section.title,
                        description: section.description,
                        prompt: section.prompt,
                        order: section.order,
                        formData: section.formData || '',
                        todoItems: (_a = section.todoItems) === null || _a === void 0 ? void 0 : _a.map(todo => ({
                            title: todo.title,
                            description: todo.description,
                            done: todo.done,
                            order: todo.order
                        }))
                    });
                })
            }));
        }
        return sanitizedForm;
    }
    /**
     * Apply form data from the tutorialFormEdit tool
     * This handles partial updates - only fields present in the tool data will be updated
     * Existing data is preserved for fields not included in the tool data
     */
    static applyToolFormData(toolData) {
        console.log('Applying tool form data:', toolData);
        if (toolData.name !== undefined) {
            this.form.name = toolData.name;
        }
        if (toolData.description !== undefined) {
            this.form.description = toolData.description;
        }
        if (toolData.introText !== undefined) {
            this.form.introText = toolData.introText;
        }
        if (toolData.tutorialGoal !== undefined) {
            this.form.tutorialGoal = toolData.tutorialGoal;
        }
        if (toolData.active !== undefined) {
            this.form.active = toolData.active;
        }
        if (toolData.categoryId !== undefined) {
            this.form.categoryId = toolData.categoryId != null ? Number(toolData.categoryId) : null;
        }
        if (toolData.source !== undefined) {
            this.form.source = toolData.source;
        }
        this.form = { ...this.form };
        // MERGE with existing steps to preserve data
        if (toolData.steps && Array.isArray(toolData.steps)) {
            this.steps = TutorialChatHelper.mergeSteps(this.steps, toolData.steps);
        }
        TutorialChatHelper.updateChatTools.call(this);
        this.notifyService.notify('tutorial.form.updatedFromAI'.t(), 'info');
    }
    /**
     * Merge tool-provided steps with existing steps, preserving existing data
     * Matches steps by order if available, otherwise by index
     */
    static mergeSteps(existingSteps, toolSteps) {
        if (!toolSteps || toolSteps.length === 0) {
            return existingSteps;
        }
        return toolSteps.map((toolStep, stepIndex) => {
            let existingStep;
            if (toolStep.order && typeof toolStep.order === 'number') {
                existingStep = existingSteps.find(s => s.order === toolStep.order);
            }
            if (!existingStep) {
                existingStep = existingSteps[stepIndex];
            }
            const mergedStep = {
                id: existingStep === null || existingStep === void 0 ? void 0 : existingStep.id, // Preserve existing ID
                title: toolStep.title !== undefined ? toolStep.title : ((existingStep === null || existingStep === void 0 ? void 0 : existingStep.title) || `${'tutorial.form.step'.t()} ${stepIndex + 1}`),
                description: toolStep.description !== undefined ? toolStep.description : ((existingStep === null || existingStep === void 0 ? void 0 : existingStep.description) || ''),
                order: toolStep.order !== undefined ? toolStep.order : ((existingStep === null || existingStep === void 0 ? void 0 : existingStep.order) || stepIndex + 1),
                sections: (existingStep === null || existingStep === void 0 ? void 0 : existingStep.sections) || []
            };
            if (toolStep.sections && Array.isArray(toolStep.sections)) {
                mergedStep.sections = TutorialChatHelper.mergeSections((existingStep === null || existingStep === void 0 ? void 0 : existingStep.sections) || [], toolStep.sections);
            }
            return mergedStep;
        });
    }
    /**
     * Merge tool-provided sections with existing sections, preserving existing data
     * Matches sections by order if available, otherwise by index
     */
    static mergeSections(existingSections, toolSections) {
        if (!toolSections || toolSections.length === 0) {
            return existingSections;
        }
        return toolSections.map((toolSection, sectionIndex) => {
            var _a, _b;
            let existingSection;
            if (toolSection.order && typeof toolSection.order === 'number') {
                existingSection = existingSections.find(s => s.order === toolSection.order);
            }
            if (!existingSection) {
                existingSection = existingSections[sectionIndex];
            }
            let mergedTodoItems = (existingSection === null || existingSection === void 0 ? void 0 : existingSection.todoItems) || [];
            if (toolSection.todoItems && Array.isArray(toolSection.todoItems)) {
                mergedTodoItems = TutorialChatHelper.mergeTodoItems((existingSection === null || existingSection === void 0 ? void 0 : existingSection.todoItems) || [], toolSection.todoItems);
            }
            // Only fields explicitly provided by the tool are updated, everything else is preserved
            return {
                id: existingSection === null || existingSection === void 0 ? void 0 : existingSection.id,
                title: toolSection.title !== undefined ? toolSection.title : ((existingSection === null || existingSection === void 0 ? void 0 : existingSection.title) || ''),
                description: toolSection.description !== undefined ? toolSection.description : ((existingSection === null || existingSection === void 0 ? void 0 : existingSection.description) || ''),
                prompt: toolSection.prompt !== undefined ? toolSection.prompt : ((existingSection === null || existingSection === void 0 ? void 0 : existingSection.prompt) || ''),
                order: toolSection.order !== undefined ? toolSection.order : ((existingSection === null || existingSection === void 0 ? void 0 : existingSection.order) || sectionIndex + 1),
                formData: toolSection.formData !== undefined ? toolSection.formData : ((existingSection === null || existingSection === void 0 ? void 0 : existingSection.formData) || ''),
                knowledgeIds: (existingSection === null || existingSection === void 0 ? void 0 : existingSection.knowledgeIds) || [],
                screenshot: (existingSection === null || existingSection === void 0 ? void 0 : existingSection.screenshot) || null,
                video: (existingSection === null || existingSection === void 0 ? void 0 : existingSection.video) || null,
                screenshotMini: (existingSection === null || existingSection === void 0 ? void 0 : existingSection.screenshotMini) || null,
                videoMini: (existingSection === null || existingSection === void 0 ? void 0 : existingSection.videoMini) || null,
                sourcePagesFrom: (_a = existingSection === null || existingSection === void 0 ? void 0 : existingSection.sourcePagesFrom) !== null && _a !== void 0 ? _a : null,
                sourcePagesTo: (_b = existingSection === null || existingSection === void 0 ? void 0 : existingSection.sourcePagesTo) !== null && _b !== void 0 ? _b : null,
                sourceText: (existingSection === null || existingSection === void 0 ? void 0 : existingSection.sourceText) || '',
                todoItems: mergedTodoItems
            };
        });
    }
    /**
     * Merge tool-provided todo items with existing todo items
     * Matches by order if available, otherwise by index
     */
    static mergeTodoItems(existingTodoItems, toolTodoItems) {
        if (!toolTodoItems || toolTodoItems.length === 0) {
            return existingTodoItems;
        }
        return toolTodoItems.map((toolTodo, todoIndex) => {
            var _a;
            let existingTodo = existingTodoItems[todoIndex];
            if (toolTodo.order && typeof toolTodo.order === 'number') {
                existingTodo = existingTodoItems.find(t => t.order === toolTodo.order);
            }
            return {
                title: toolTodo.title !== undefined ? toolTodo.title : ((existingTodo === null || existingTodo === void 0 ? void 0 : existingTodo.title) || ''),
                description: toolTodo.description !== undefined ? toolTodo.description : ((existingTodo === null || existingTodo === void 0 ? void 0 : existingTodo.description) || ''),
                done: toolTodo.done !== undefined ? toolTodo.done : ((_a = existingTodo === null || existingTodo === void 0 ? void 0 : existingTodo.done) !== null && _a !== void 0 ? _a : false),
                order: toolTodo.order !== undefined ? toolTodo.order : ((existingTodo === null || existingTodo === void 0 ? void 0 : existingTodo.order) || todoIndex + 1)
            };
        });
    }
}
exports.TutorialChatHelper = TutorialChatHelper;
